import { gql } from "@apollo/client";
import { Button, getActionMenuColumn } from "@resource/atlas";
import { useLogEvent } from "analytics";
import { useAuthContext } from "auth/context";
import { useCurrentQueryParamStateWithDefaults } from "components/Table/hooks/useQueryParamState";
import PaginatedTable from "components/Table/PaginatedTable";
import { PaginatedTableFieldFilterTypes } from "components/Table/PaginatedTable/types";
import TemplateTableNameCell from "components/TemplateTables/TemplateTableNameCell";
import UserTimeAgoTableCell from "components/UserTimeAgoTableCell";
import {
  ArchivePostTemplate,
  ArchivePostTemplateVariables,
  OrderByArg,
  PostTemplatesFilterData,
  PostTemplatesPaginatedVariables,
  PostTemplatesTableRowFragment as PostTemplate,
  PostTemplateStatusEnum,
  UnarchivePostTemplate,
  UnarchivePostTemplateVariables,
} from "generated/schemaTypes";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { CellProps, Column } from "react-table";
import { formatEntity } from "shared/constants/entities";
import { PermissionEnum } from "utils/permissions";
import useMutation from "utils/useMutation";

import { POST_TEMPLATE_FOR_MANAGING_FRAGMENT } from "./ManagementDialogs/queries";
import { removePostTemplateFromCurrentCachedList } from "./utils";

const USER_MEMBERSHIP_FRAGMENT = gql`
  fragment PostTemplatesTableUserMembershipFragment on UserMembership {
    id
    user {
      id
      name: fullName
      imageUrl
    }
  }
`;

const POST_TEMPLATES_TABLE_ROW_FRAGMENT = gql`
  fragment PostTemplatesTableRowFragment on PostTemplate {
    id
    name
    createdAt
    updatedAt
    archivedAt
    status
    ...PostTemplateForManagingFragment
    createdBy {
      ...PostTemplatesTableUserMembershipFragment
    }
    updatedBy {
      ...PostTemplatesTableUserMembershipFragment
    }
    department {
      id
      name
    }
    organization {
      id
    }
  }
  ${USER_MEMBERSHIP_FRAGMENT}
  ${POST_TEMPLATE_FOR_MANAGING_FRAGMENT}
`;

const ARCHIVED_POST_TEMPLATE_RESPONSE_FRAGMENT = gql`
  fragment ArchivedPostTemplateResponseFragment on PostTemplate {
    id
    archivedAt
    status
    organization {
      id
    }
  }
`;

const ARCHIVE_POST_TEMPLATE = gql`
  mutation ArchivePostTemplate($input: ArchivePostTemplateInput!) {
    archivePostTemplate(input: $input) {
      success
      message
      postTemplate {
        ...ArchivedPostTemplateResponseFragment
      }
    }
  }
  ${ARCHIVED_POST_TEMPLATE_RESPONSE_FRAGMENT}
`;

const UNARCHIVE_POST_TEMPLATE = gql`
  mutation UnarchivePostTemplate($input: UnarchivePostTemplateInput!) {
    unarchivePostTemplate(input: $input) {
      success
      message
      postTemplate {
        ...ArchivedPostTemplateResponseFragment
      }
    }
  }
  ${ARCHIVED_POST_TEMPLATE_RESPONSE_FRAGMENT}
`;

type PostTemplatesTableProps = {
  postTemplates: PostTemplate[];
  loading: boolean;
  tableVariables: PostTemplatesPaginatedVariables;
  allIds: string[];
  onRowClick?: (postTemplate: PostTemplate) => void;
  onDuplicate: (postTemplate: PostTemplate) => void;
  onCreateNew?: () => void;
  onSettingsActionMenuItemClick: (postTemplate: PostTemplate) => void;
  filterData?: PostTemplatesFilterData;
};

function PostTemplatesTable({
  postTemplates,
  loading,
  tableVariables,
  allIds,
  onRowClick,
  onCreateNew,
  onSettingsActionMenuItemClick,
  filterData,
  onDuplicate,
}: PostTemplatesTableProps) {
  const { sortBy } = useCurrentQueryParamStateWithDefaults({
    sortBy: [{ id: "name", desc: false }],
  });
  const { checkRolePermissions } = useAuthContext();
  const logEvent = useLogEvent({
    component: "PostTemplatesTable",
    project: ["Zeus", "Post templates"],
  });

  const [archivePostTemplate] = useMutation<
    ArchivePostTemplate,
    ArchivePostTemplateVariables
  >(ARCHIVE_POST_TEMPLATE, {
    update: (cache, archivePostTemplateResult) => {
      if (
        !archivePostTemplateResult.data?.archivePostTemplate?.postTemplate ||
        !tableVariables.status ||
        tableVariables.status === PostTemplateStatusEnum.ALL
      ) {
        return;
      }
      removePostTemplateFromCurrentCachedList({
        cache,
        postTemplate:
          archivePostTemplateResult.data?.archivePostTemplate?.postTemplate,
        status: tableVariables.status ?? PostTemplateStatusEnum.ALL,
      });
    },
  });

  const [unarchivePostTemplate] = useMutation<
    UnarchivePostTemplate,
    UnarchivePostTemplateVariables
  >(UNARCHIVE_POST_TEMPLATE, {
    update: (cache, unarchivePostTemplateResult) => {
      if (
        !unarchivePostTemplateResult.data?.unarchivePostTemplate
          ?.postTemplate ||
        !tableVariables.status ||
        tableVariables.status === PostTemplateStatusEnum.ALL
      ) {
        return;
      }
      removePostTemplateFromCurrentCachedList({
        cache,
        postTemplate:
          unarchivePostTemplateResult.data?.unarchivePostTemplate?.postTemplate,
        status: tableVariables.status,
      });
    },
  });

  const getPostTemplatesActionMenuColumn = useCallback(
    () =>
      getActionMenuColumn({
        getItems: ({ row }: CellProps<PostTemplate>) => [
          {
            _type: "item",
            children: "Duplicate",
            size: "compact",
            onClick: () => {
              logEvent("Duplicate Post Template Clicked");
              onDuplicate(row.original);
            },
          },
          {
            _type: "item",
            children: "Edit",
            size: "compact",
            onClick: () => onRowClick?.(row.original),
          },
          {
            _type: "item",
            children: "Settings",
            size: "compact",
            onClick: () => onSettingsActionMenuItemClick(row.original),
          },
          {
            _type: "separator",
          },
          row.original?.archivedAt
            ? {
                _type: "item",
                children: "Restore",
                disabled: !checkRolePermissions(PermissionEnum.TEMPLATE_WRITE),
                size: "compact",
                onClick: () => {
                  logEvent("Unarchive post template clicked");
                  unarchivePostTemplate({
                    variables: {
                      input: {
                        id: row.original.id,
                      },
                    },
                  });
                },
              }
            : {
                _type: "item",
                children: "Archive",
                disabled: !checkRolePermissions(PermissionEnum.TEMPLATE_WRITE),
                size: "compact",
                onClick: () => {
                  logEvent("Archive post template clicked");
                  archivePostTemplate({
                    variables: {
                      input: {
                        id: row.original.id,
                      },
                    },
                  });
                },
              },
        ],
      }),
    [
      checkRolePermissions,
      logEvent,
      onDuplicate,
      onRowClick,
      onSettingsActionMenuItemClick,
      unarchivePostTemplate,
      archivePostTemplate,
    ]
  );

  const columns = useMemo<Column<PostTemplate>[]>(
    () => [
      {
        id: "name",
        width: 250,
        Header: "Name",
        Cell: TemplateTableNameCell,
        accessor: "name",
      },
      {
        id: "updatedAt",
        width: 250,
        disableResizing: true,
        Header: "Last modified",
        Cell: UserTimeAgoTableCell,
        accessor: ({
          updatedBy,
          createdBy,
          createdAt,
          updatedAt,
        }: PostTemplate): {
          user?: {
            name: string;
            imageUrl?: string;
          };
          date: string;
        } => ({
          user: updatedBy?.user || createdBy?.user,
          date: updatedAt || createdAt,
        }),
      },
      {
        id: "departmentIds",
        width: 0,
        hidden: true,
      },
      {
        id: "status",
        width: 0,
        hidden: true,
      },
      getPostTemplatesActionMenuColumn(),
    ],
    [getPostTemplatesActionMenuColumn]
  );

  return (
    <PaginatedTable<PostTemplate>
      data={postTemplates}
      loading={loading}
      paginationProps={{
        ..._.pick(tableVariables, ["limit", "offset"]),
      }}
      filters={{
        orderBy: _(sortBy)
          .map(({ id, desc }) => ({
            [id]: desc ? OrderByArg.desc : OrderByArg.asc,
          }))
          .value(),
        field: [
          {
            id: "departmentIds",
            label: "Department",
            type: PaginatedTableFieldFilterTypes.multiSelect,
            defaultValue: null,
            initialValue: tableVariables.departmentIds,
            options:
              filterData?.atsDepartments?.map(({ name, id }) => ({
                id,
                caption: name,
              })) ?? [],
            disabled: loading,
          },
          {
            id: "status",
            label: "Status",
            type: PaginatedTableFieldFilterTypes.singleSelect,
            defaultValue: PostTemplateStatusEnum.ACTIVE,
            initialValue: tableVariables.status,
            options: [
              {
                id: PostTemplateStatusEnum.ALL,
                caption: "All",
              },
              {
                id: PostTemplateStatusEnum.ACTIVE,
                caption: "Active",
              },
              {
                id: PostTemplateStatusEnum.ARCHIVED,
                caption: "Archived",
              },
            ],
            disabled: loading,
          },
        ],
      }}
      allIds={allIds}
      totalRecordCount={allIds.length}
      columns={columns}
      getRowId={(postTemplate) => postTemplate.id}
      onRowClick={(row) => onRowClick?.(row.original)}
      headerRightActions={
        <Button variant="primary" onClick={onCreateNew}>
          New {formatEntity("message")} template
        </Button>
      }
    />
  );
}

PostTemplatesTable.fragments = {
  postTemplate: POST_TEMPLATES_TABLE_ROW_FRAGMENT,
};

export default PostTemplatesTable;
